import { SeeqNames } from 'generated-seeqnames/app.constants.seeqnames';
import _ from 'lodash';
import moment from 'moment-timezone';
import { useEffect } from 'react';
import { createSelector } from 'reselect';
import { useRegisterPersistedStore } from '@/stores/PersistenceProvider';
import { TREND_TOOLS } from '@/toolSelection/investigate.constants';
import {
  DEFAULT_BOOTSTRAP,
  DEFAULT_CONTAMINATION,
  DEFAULT_NUM_ESTIMATORS,
  INPUT_NAMES,
} from '@/tools/isolationForestV2/isolationForest.constants';
import { BASE_TOOL_COMMON_PROPS } from '@/tools/tool.constants';
import { getToolConfigParams, sanitizeToolItem } from '@/tools/tool.utilities';
import { type ActionsFactory, type ResettableStoreState, createStore, createStoreInstance } from '@seeqdev/utils';
import type { MappedInputFeatureV1 } from '@/tools/models/templateInstance.utilities';
import type {
  BaseToolPersistedState,
  ModelRehydratePayload,
  ToolConfigParams,
  ToolConfigValue,
  ToolItem,
} from '@/tools/tool.types';
import type { ValueWithUnitsItem } from '@/trend/ValueWithUnits.atom';

export const DEFAULT_DURATION = { value: 0, units: 'min' };

export interface IsolationForestV2StoreState extends BaseToolPersistedState {
  windowStart: number;
  windowEnd: number;
  outputsCollapsed: boolean;
  modelPropertiesExpanded: boolean;
  color: string;
  numEstimators: number;
  contaminationAuto: boolean;
  contamination: number;
  bootstrap: boolean;
  cleanCapsules: boolean;
  capsuleMinDuration: ValueWithUnitsItem;
  capsuleMergeDuration: ValueWithUnitsItem;
  inputSignals: ToolItem[];
  condition?: ToolItem | null;
  restrictToCondition?: ToolItem | null;
  tableApplicationId?: string;
  instanceId?: string;
}

export interface IsolationForestV2StoreActions {
  setSearchName: (name: string) => void;
  setAdvancedParametersCollapsed: (collapsed: boolean) => void;
  setOutputsCollapsed: (collapsed: boolean) => void;
  setTrainingWindow: (window: { startTime: number; endTime: number }) => void;
  setModelPropertiesExpanded: (expanded: boolean) => void;
  setNumEstimators: (numEstimators: number) => void;
  setContaminationAuto: (contaminationAuto: boolean) => void;
  setContamination: (contamination: number) => void;
  setBootstrap: (bootstrap: boolean) => void;
  setCleanCapsules: (cleanCapsules: boolean) => void;
  setCapsuleMinDuration: (duration: ValueWithUnitsItem) => void;
  setCapsuleMergeDuration: (duration: ValueWithUnitsItem) => void;
  addInputSignal: (signal: ToolItem) => void;
  removeInputSignal: (signal: ToolItem) => void;
  setCondition: (condition: ToolItem | null | undefined) => void;
  setRestrictToCondition: (condition: ToolItem | null | undefined) => void;
  rehydrateForEdit: (payload: ModelRehydratePayload) => void;
  reset: ResettableStoreState<IsolationForestV2StoreState>;
}

export type IsolationForestDehydratedStateV2 = IsolationForestV2StoreState;

const ITEM_PARAMETER_KEYS = ['inputSignals', 'condition', 'restrictToCondition'];

const initialState: IsolationForestV2StoreState = Object.freeze({
  ...BASE_TOOL_COMMON_PROPS,
  windowStart: 0,
  windowEnd: 0,
  outputsCollapsed: true,
  modelPropertiesExpanded: false,
  color: '',
  numEstimators: DEFAULT_NUM_ESTIMATORS,
  contaminationAuto: true,
  contamination: DEFAULT_CONTAMINATION,
  bootstrap: DEFAULT_BOOTSTRAP,
  cleanCapsules: false,
  capsuleMinDuration: { value: 0, units: 'min' } as ValueWithUnitsItem,
  capsuleMergeDuration: { value: 0, units: 'min' } as ValueWithUnitsItem,
  inputSignals: [] as ToolItem[],
  condition: undefined as ToolItem | null | undefined,
  restrictToCondition: undefined as ToolItem | null | undefined,
  tableApplicationId: undefined as string | undefined,
  instanceId: undefined as string | undefined,
});

const isolationForestV2ConfigParamsSelector = createSelector(
  [(state: IsolationForestV2StoreState) => state],
  (state): ToolConfigParams => {
    const config = getToolConfigParams({
      serializedState: state as unknown as Record<string, ToolConfigValue>,
      toolType: TREND_TOOLS.ISOLATION_FOREST_V_2,
      parameterDefinitionKeys: ITEM_PARAMETER_KEYS,
      advancedParametersCollapsed: state.advancedParametersCollapsed,
    });
    return _.omit(config, ['modelPropertiesExpanded']) as ToolConfigParams;
  },
);

const isolationForestV2WindowSelector = createSelector(
  [(state: IsolationForestV2StoreState) => state.windowStart, (state: IsolationForestV2StoreState) => state.windowEnd],
  (windowStart, windowEnd) => ({ startTime: windowStart, endTime: windowEnd }),
);

export const dehydrateIsolationForestV2StoreState = (
  state: IsolationForestV2StoreState,
): IsolationForestDehydratedStateV2 => state;

const isolationForestV2ActionsFactory: ActionsFactory<IsolationForestV2StoreState, IsolationForestV2StoreActions> = (
  set,
  _get,
  resetState,
) => ({
  setSearchName: (name) =>
    set((draft) => {
      draft.name = name;
    }),

  setAdvancedParametersCollapsed: (collapsed) =>
    set((draft) => {
      draft.advancedParametersCollapsed = collapsed;
    }),

  setOutputsCollapsed: (collapsed) =>
    set((draft) => {
      draft.outputsCollapsed = collapsed;
    }),

  setTrainingWindow: (window) =>
    set((draft) => {
      draft.windowStart = window.startTime;
      draft.windowEnd = window.endTime;
    }),

  setModelPropertiesExpanded: (expanded) =>
    set((draft) => {
      draft.modelPropertiesExpanded = expanded;
    }),

  setNumEstimators: (numEstimators) =>
    set((draft) => {
      draft.numEstimators = numEstimators;
    }),

  setContaminationAuto: (contaminationAuto) =>
    set((draft) => {
      draft.contaminationAuto = contaminationAuto;
    }),

  setContamination: (contamination) =>
    set((draft) => {
      draft.contamination = contamination;
    }),

  setBootstrap: (bootstrap) =>
    set((draft) => {
      draft.bootstrap = bootstrap;
    }),

  setCleanCapsules: (cleanCapsules) =>
    set((draft) => {
      draft.cleanCapsules = cleanCapsules;
    }),

  setCapsuleMinDuration: (duration) =>
    set((draft) => {
      draft.capsuleMinDuration = duration;
    }),

  setCapsuleMergeDuration: (duration) =>
    set((draft) => {
      draft.capsuleMergeDuration = duration;
    }),

  addInputSignal: (signal) =>
    set((draft) => {
      const sanitized = sanitizeToolItem(signal);
      if (sanitized && !draft.inputSignals.some((s) => s.id === sanitized.id)) {
        draft.inputSignals = draft.inputSignals.concat(sanitized);
      }
    }),

  removeInputSignal: (signal) =>
    set((draft) => {
      draft.inputSignals = draft.inputSignals.filter((s) => s.id !== signal?.id);
    }),

  setCondition: (item) =>
    set((draft) => {
      draft.condition = sanitizeToolItem(item) as IsolationForestV2StoreState['condition'];
    }),

  setRestrictToCondition: (item) =>
    set((draft) => {
      draft.restrictToCondition = sanitizeToolItem(item) as IsolationForestV2StoreState['restrictToCondition'];
    }),

  rehydrateForEdit: (payload: ModelRehydratePayload) => {
    resetState();
    const inputSignals: ToolItem[] = [];
    set((draft) => {
      draft.id = payload.id;
      draft.instanceId = payload.instanceId;
      draft.tableApplicationId = payload.instanceData?.tableApplicationId;
      (payload.modelData?.inputFeatures ?? []).forEach((feature: MappedInputFeatureV1): void => {
        const inputName: string = feature.name!;
        switch (inputName) {
          case SeeqNames.Models.InstanceNameInputFeatureName:
            // TODO: CRAB-53864: Handle duplicate names more gracefully
            draft.name = payload.instanceId ? feature.inputText : `${feature.inputText} (Copy)`;
            break;
          case INPUT_NAMES.numEstimatorsInput:
            draft.numEstimators = feature.inputNumeric ?? DEFAULT_NUM_ESTIMATORS;
            break;
          case INPUT_NAMES.contaminationInput:
            draft.contamination = feature.inputNumeric ?? DEFAULT_CONTAMINATION;
            draft.contaminationAuto = _.isNil(feature.inputNumeric);
            break;
          case INPUT_NAMES.bootstrapInput:
            draft.bootstrap = feature.inputBoolean ?? DEFAULT_BOOTSTRAP;
            break;
          case INPUT_NAMES.startTimeInput: {
            const startTimeInput = JSON.parse(feature.inputText!);
            draft.windowStart = moment(startTimeInput).valueOf();
            break;
          }
          case INPUT_NAMES.endTimeInput: {
            const endTimeInput = JSON.parse(feature.inputText!);
            draft.windowEnd = moment(endTimeInput).valueOf();
            break;
          }
          case INPUT_NAMES.mergeCapsuleDurationInput: {
            draft.cleanCapsules = !_.isNil(feature.inputNumeric);
            draft.capsuleMergeDuration = {
              value: Number(feature.inputNumeric ?? 0),
              units: feature.inputNumericUom ?? 'min',
            };
            break;
          }
          case INPUT_NAMES.minCapsuleDurationInput: {
            draft.cleanCapsules = !_.isNil(feature.inputNumeric);
            draft.capsuleMinDuration = {
              value: Number(feature.inputNumeric ?? 0),
              units: feature.inputNumericUom ?? 'min',
            };
            break;
          }
          case INPUT_NAMES.trainWithinConditionInput:
            if (feature.inputItem) {
              draft.condition = { id: feature.inputItem };
            }
            break;
          case INPUT_NAMES.restrictOutputToConditionInput: {
            if (feature.inputItem) {
              draft.restrictToCondition = { id: feature.inputItem };
            }
            break;
          }
          default:
            if (inputName.startsWith(INPUT_NAMES.signalInput) && feature.inputItem) {
              inputSignals.push({ id: feature.inputItem });
            }
            break;
        }
      });
      draft.inputSignals = inputSignals;
    });
  },

  reset: (overrides) => resetState(overrides),
});

const [IsolationForestV2StoreProvider, useIsolationForestV2StoreInstance, useIsolationForestV2StoreSelector] =
  createStore('IsolationForestV2', initialState, isolationForestV2ActionsFactory);

export { IsolationForestV2StoreProvider };

export const useIsolationForestV2ToolId = () => useIsolationForestV2StoreSelector((state) => state.id);
export const useIsolationForestV2ToolName = () => useIsolationForestV2StoreSelector((state) => state.name);
export const useIsolationForestV2ToolOriginalParameters = () =>
  useIsolationForestV2StoreSelector((state) => state.originalParameters);
export const useIsolationForestV2ToolAdvancedParametersCollapsed = () =>
  useIsolationForestV2StoreSelector((state) => state.advancedParametersCollapsed);
export const useIsolationForestV2ToolOutputsCollapsed = () =>
  useIsolationForestV2StoreSelector((state) => state.outputsCollapsed);
export const useIsolationForestV2ToolWindow = () => useIsolationForestV2StoreSelector(isolationForestV2WindowSelector);
export const useIsolationForestV2ToolModelPropertiesExpanded = () =>
  useIsolationForestV2StoreSelector((state) => state.modelPropertiesExpanded);
export const useIsolationForestV2ToolNumEstimators = () =>
  useIsolationForestV2StoreSelector((state) => state.numEstimators);
export const useIsolationForestV2ToolContaminationAuto = () =>
  useIsolationForestV2StoreSelector((state) => state.contaminationAuto);
export const useIsolationForestV2ToolContamination = () =>
  useIsolationForestV2StoreSelector((state) => state.contamination);
export const useIsolationForestV2ToolBootstrap = () => useIsolationForestV2StoreSelector((state) => state.bootstrap);
export const useIsolationForestV2ToolCleanCapsules = () =>
  useIsolationForestV2StoreSelector((state) => state.cleanCapsules);
export const useIsolationForestV2ToolCapsuleMinDuration = () =>
  useIsolationForestV2StoreSelector((state) => state.capsuleMinDuration);
export const useIsolationForestV2ToolCapsuleMergeDuration = () =>
  useIsolationForestV2StoreSelector((state) => state.capsuleMergeDuration);
export const useIsolationForestV2ToolInputSignals = () =>
  useIsolationForestV2StoreSelector((state) => state.inputSignals);
export const useIsolationForestV2ToolCondition = () => useIsolationForestV2StoreSelector((state) => state.condition);
export const useIsolationForestV2ToolRestrictToCondition = () =>
  useIsolationForestV2StoreSelector((state) => state.restrictToCondition);
export const useIsolationForestV2ToolInstanceId = () => useIsolationForestV2StoreSelector((state) => state.instanceId);
export const useIsolationForestV2ToolTableApplicationId = () =>
  useIsolationForestV2StoreSelector((state) => state.tableApplicationId);
export const useIsolationForestV2ToolConfigParams = () =>
  useIsolationForestV2StoreSelector(isolationForestV2ConfigParamsSelector);

export const useIsolationForestV2ToolActions = () => useIsolationForestV2StoreInstance().actions;

/**
 * @deprecated This function is ONLY for legacy migration from Flux/Baobab stores. Prefer
 * `<TestStoreProvider>` or `<*StoreProvider>` with UI-driven assertions instead.
 */
export function createTestIsolationForestV2Store(overrides?: Partial<IsolationForestV2StoreState>) {
  if (process.env.NODE_ENV !== 'test') {
    throw new Error('createTestIsolationForestV2Store() should only be used in tests.');
  }
  return createStoreInstance({ ...initialState, ...overrides }, isolationForestV2ActionsFactory);
}

/**
 * @deprecated Legacy singleton bridge for non-React consumers (utility files)
 * that cannot use hooks. New code should use the hooks above and the Provider.
 */
export function legacyCreateIsolationForestV2Store() {
  return createStoreInstance<IsolationForestV2StoreState, IsolationForestV2StoreActions>(
    initialState,
    isolationForestV2ActionsFactory,
  );
}

export const usePersistIsolationForestV2Store = () => {
  const register = useRegisterPersistedStore();
  const store = useIsolationForestV2StoreInstance();

  useEffect(() => {
    return register({
      storeName: 'isolationForestV2Store',
      persistenceLevel: 'WORKSHEET',
      store,
      dehydrate: dehydrateIsolationForestV2StoreState,
    });
  }, [register, store]);
};
